import type { DetailsValues } from './detailsValues';
import { descriptionText } from './detailsView';

/**
 * What's changed (brief §3, right panel): every saved change, newest first —
 * which card, what changed, who, when.
 *
 * Kept as structured items, not sentences, so the panel words them in the
 * current language, and people are stored by id so a renamed name follows.
 * The counts behind "Can log time: 10 → 200 people" are taken at save time:
 * that is what the change meant then.
 */
export type ChangeCard = 'about' | 'people' | 'logTime' | 'when' | 'belongs';

export type ChangeItem =
  | { kind: 'descriptionUpdated' }
  | { kind: 'descriptionRemoved' }
  | { kind: 'fileAdded'; name: string }
  | { kind: 'fileRemoved'; name: string }
  | { kind: 'linkAdded'; name: string }
  | { kind: 'linkRemoved'; name: string }
  | {
      kind: 'person';
      role: 'projectManager' | 'accountManager' | 'partner' | 'contact';
      from: string | null;
      to: string | null;
    }
  | { kind: 'canLogTime'; before: number; after: number }
  | { kind: 'askForEtc'; on: boolean }
  | { kind: 'period'; startDate: string; endDate: string }
  | { kind: 'delivery'; date: string }
  | {
      kind: 'organisation';
      field: 'legalEntity' | 'department' | 'projectType' | 'category';
      from: string | null;
      to: string | null;
    }
  | { kind: 'planner'; on: boolean }
  | { kind: 'likelihood'; from: number; to: number };

export type ChangeEntry = {
  id: string;
  card: ChangeCard;
  items: ChangeItem[];
  /** ISO date-time of the save. */
  at: string;
};

/** How many could log time before and after — computed by the caller. */
export type LogTimeCounts = { before: number; after: number };

/**
 * What a save of `card` changed, from `before` to `after`. Empty when the save
 * changed nothing — then no entry is written.
 */
export const diffChanges = (
  card: ChangeCard,
  before: DetailsValues,
  after: DetailsValues,
  counts?: LogTimeCounts,
): ChangeItem[] => {
  const items: ChangeItem[] = [];
  switch (card) {
    case 'about': {
      if (before.description !== after.description) {
        items.push({
          kind:
            descriptionText(after.description) === '' ? 'descriptionRemoved' : 'descriptionUpdated',
        });
      }
      const had = (list: { id: string }[], id: string) => list.some((entry) => entry.id === id);
      for (const file of after.files)
        if (!had(before.files, file.id)) items.push({ kind: 'fileAdded', name: file.name });
      for (const file of before.files)
        if (!had(after.files, file.id)) items.push({ kind: 'fileRemoved', name: file.name });
      for (const link of after.links)
        if (!had(before.links, link.id)) items.push({ kind: 'linkAdded', name: link.name });
      for (const link of before.links)
        if (!had(after.links, link.id)) items.push({ kind: 'linkRemoved', name: link.name });
      break;
    }
    case 'people': {
      const roles = [
        ['projectManager', 'projectManagerId'],
        ['accountManager', 'accountManagerId'],
        ['partner', 'partnerId'],
        ['contact', 'contactId'],
      ] as const;
      for (const [role, field] of roles) {
        if (before[field] !== after[field]) {
          items.push({ kind: 'person', role, from: before[field], to: after[field] });
        }
      }
      break;
    }
    case 'logTime':
      if (before.logTimeScope !== after.logTimeScope && counts) {
        items.push({ kind: 'canLogTime', before: counts.before, after: counts.after });
      }
      if (before.askForEtc !== after.askForEtc)
        items.push({ kind: 'askForEtc', on: after.askForEtc });
      break;
    case 'when':
      if (before.startDate !== after.startDate || before.endDate !== after.endDate) {
        items.push({ kind: 'period', startDate: after.startDate, endDate: after.endDate });
      }
      if (before.deliveryDate !== after.deliveryDate) {
        items.push({ kind: 'delivery', date: after.deliveryDate });
      }
      if (before.includeInResourcePlanning !== after.includeInResourcePlanning) {
        items.push({ kind: 'planner', on: after.includeInResourcePlanning });
      }
      if (before.likelihoodPct !== after.likelihoodPct) {
        items.push({ kind: 'likelihood', from: before.likelihoodPct, to: after.likelihoodPct });
      }
      break;
    case 'belongs': {
      const fields = [
        ['legalEntity', 'legalEntityId'],
        ['department', 'departmentId'],
        ['projectType', 'projectType'],
        ['category', 'projectCategory'],
      ] as const;
      for (const [field, key] of fields) {
        if (before[key] !== after[key]) {
          items.push({ kind: 'organisation', field, from: before[key], to: after[key] });
        }
      }
      break;
    }
  }
  return items;
};

/** A stored log, entry by entry; anything unreadable is dropped. */
export const parseChangeLog = (raw: unknown): ChangeEntry[] =>
  Array.isArray(raw)
    ? raw.filter(
        (entry): entry is ChangeEntry =>
          typeof entry === 'object' &&
          entry !== null &&
          typeof (entry as ChangeEntry).id === 'string' &&
          typeof (entry as ChangeEntry).card === 'string' &&
          typeof (entry as ChangeEntry).at === 'string' &&
          Array.isArray((entry as ChangeEntry).items),
      )
    : [];
